import React, { useState, useRef, useEffect } from "react";
import Background from './components/Background/Background';

// Utility functions
const ALLOWED_FILE_TYPES = [
  'text/plain', 
  'application/pdf', 
  'application/msword', 
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
  'text/rtf'
];

const ALLOWED_FILE_EXTENSIONS = ['.txt', '.rtf'];

const isValidFile = (file) => {
  return ALLOWED_FILE_TYPES.includes(file.type) || 
         ALLOWED_FILE_EXTENSIONS.some(ext => file.name.endsWith(ext));
};

const validateFiles = (files) => {
  return files.filter(isValidFile);
};

// LocalStorage utilities
const STORAGE_KEYS = {
  COMPANY_NAME: 'companyName',
  COMPANY_CONTACT_PERSON: 'companyContactPerson', 
  COMPANY_PHONE: 'companyPhone',
  COMPANY_EMAIL: 'companyEmail',
  COMPANY_ADDRESS: 'companyAddress',
  COMPANY_ADDRESS_LINE2: 'companyAddress2',
  COMPANY_WEBSITE: 'companyWebsite',
};

const getStoredCompanyInfo = () => ({
  name: localStorage.getItem(STORAGE_KEYS.COMPANY_NAME) || '',
  contactPerson: localStorage.getItem(STORAGE_KEYS.COMPANY_CONTACT_PERSON) || '',
  phone: localStorage.getItem(STORAGE_KEYS.COMPANY_PHONE) || '',
  email: localStorage.getItem(STORAGE_KEYS.COMPANY_EMAIL) || '',
  address: localStorage.getItem(STORAGE_KEYS.COMPANY_ADDRESS) || '',
  addressLine2: localStorage.getItem(STORAGE_KEYS.COMPANY_ADDRESS_LINE2) || '',
  website: localStorage.getItem(STORAGE_KEYS.COMPANY_WEBSITE) || '',
});

// Removed unused helper function to satisfy CI lint rules

// Main App Component
export default function App() {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFilename, setPdfFilename] = useState("DesignBrief.pdf");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedWordsEstimate, setUploadedWordsEstimate] = useState(0);
  const fileInputRef = useRef();
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  
  const [modalState, setModalState] = useState('closed'); // 'closed', 'preparing', 'opening', 'open', 'closing'
  const [buttonRect, setButtonRect] = useState(null);
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(getStoredCompanyInfo());

  // This useEffect ensures the animation only starts AFTER we have the button's position.
  // This prevents the "flash at top-left" bug.
  useEffect(() => {
    if (buttonRect) {
      setModalState('preparing');
    }
  }, [buttonRect, setModalState]);

  // When the modal is fully closed, reset the buttonRect.
  useEffect(() => {
    if (modalState === 'closed') {
      setButtonRect(null);
    }
  }, [modalState]);


  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;
    
    const validFiles = validateFiles(newFiles);

    if (validFiles.length !== newFiles.length) {
      setError("Some files were not valid document types (.txt, .pdf, .doc, .docx, .rtf) and were ignored.");
    } else {
      setError("");
    }

    setUploadedFiles(prevFiles => {
      const newFiles = [...prevFiles, ...validFiles];
      updateWordCountEstimate(newFiles);
      return newFiles;
    });
    
    // Clear the input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Estimate word count from file size (very conservative approximation)
  const estimateWordCountFromFile = (file) => {
    const sizeInKB = file.size / 1024;
    // Very conservative estimates - PDFs especially can have lots of formatting overhead
    if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
      return Math.round(sizeInKB * 100); // ~100 words per KB for plain text
    } else if (file.type === 'application/pdf') {
      return Math.round(sizeInKB * 10); // Very conservative for PDF - lots of formatting overhead
    } else if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
      return Math.round(sizeInKB * 20); // Conservative for Word docs
    } else if (file.name.endsWith('.rtf')) {
      return Math.round(sizeInKB * 15); // Conservative for RTF
    }
    return Math.round(sizeInKB * 15); // Default conservative estimate
  };

  const updateWordCountEstimate = (files) => {
    const totalEstimate = files.reduce((total, file) => {
      return total + estimateWordCountFromFile(file);
    }, 0);
    setUploadedWordsEstimate(totalEstimate);
  };

  const removeFile = (fileToRemove) => {
    setUploadedFiles(prevFiles => {
      const newFiles = prevFiles.filter(file => file !== fileToRemove);
      updateWordCountEstimate(newFiles);
      return newFiles;
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPdfUrl("");
    setPdfFilename("DesignBrief.pdf"); // Reset filename
    
    if (!notes.trim() && uploadedFiles.length === 0) {
      setError("Please enter client notes or upload at least one document.");
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('notes', notes);
      
      uploadedFiles.forEach(file => {
        formData.append('documents', file);
      });

      // Add company info if available
      if (companyInfo.name || companyInfo.contactPerson || companyInfo.phone || companyInfo.email || companyInfo.website || companyInfo.address || companyInfo.addressLine2) {
        formData.append('companyInfo', JSON.stringify(companyInfo));
      }

      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocalhost
        ? 'http://localhost:5100/archibrief-28683/us-central1/generatePdf'
        : '/api/generatePdf';

      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate PDF");
      }

      // Extract filename from headers
      const filename = response.headers.get('X-PDF-Filename');
      
      console.log('Received filename:', filename); // Debug log
      setPdfFilename(filename || 'DesignBrief.pdf');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Stripe checkout function
  const handleStripeCheckout = async () => {
    try {
      setLoading(true);
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocalhost
        ? 'http://localhost:5100/archibrief-28683/us-central1/createCheckoutSession'
        : '/api/createCheckoutSession';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: 'price_1SEyjzAByl9ndWbs9qtQlap1', // Your actual Stripe Price ID
          userEmail: companyInfo.email || undefined,
          userId: 'anonymous', // You can use Firebase Auth user ID if available
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      setError(err.message || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Background />
      <div className="min-h-screen flex flex-col relative">
        {/* Header */}
        <header className="backdrop-blur-sm border-b border-white/20 sticky top-0 z-10 shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-center">
            <h1 className="text-2xl font-extrabold text-white tracking-tight drop-shadow-lg">Archibrief</h1>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          {/* Company Info Section */}
          <div className="w-full max-w-2xl mb-4">
            <button
              onClick={() => setShowCompanyInfo(!showCompanyInfo)}
              className="w-full px-4 py-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow border border-gray-100 text-left flex items-center justify-between text-gray-700 hover:bg-white/90 transition"
            >
              <span className="flex items-center gap-3">
                <span className="text-xs text-gray-400 transition-transform duration-200" style={{ transform: showCompanyInfo ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  ▶
                </span>
                Company Footer Info (Optional)
              </span>
              <span className="text-sm text-gray-500">
                {companyInfo.name ? companyInfo.name : 'Not set'}
              </span>
            </button>

            {showCompanyInfo && (
              <div className="mt-2 p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow border border-gray-100 animate-slideDownSmooth">
                <div className="space-y-3">
                  <input
                    type="text"
                    value={companyInfo.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setCompanyInfo(prev => ({...prev, name: newName}));
                      localStorage.setItem('companyName', newName);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Company Name"
                  />
                  
                  <input
                    type="text"
                    value={companyInfo.contactPerson}
                    onChange={(e) => {
                      const newContactPerson = e.target.value;
                      setCompanyInfo(prev => ({...prev, contactPerson: newContactPerson}));
                      localStorage.setItem('companyContactPerson', newContactPerson);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Contact Person"
                  />
                  
                  <input
                    type="tel"
                    value={companyInfo.phone}
                    onChange={(e) => {
                      const newPhone = e.target.value;
                      setCompanyInfo(prev => ({...prev, phone: newPhone}));
                      localStorage.setItem('companyPhone', newPhone);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Phone Number"
                  />
                  
                  <input
                    type="email"
                    value={companyInfo.email}
                    onChange={(e) => {
                      const newEmail = e.target.value;
                      setCompanyInfo(prev => ({...prev, email: newEmail}));
                      localStorage.setItem('companyEmail', newEmail);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Email Address"
                  />
                  
                  <input
                    type="text"
                    value={companyInfo.address}
                    onChange={(e) => {
                      const newAddress = e.target.value;
                      setCompanyInfo(prev => ({...prev, address: newAddress}));
                      localStorage.setItem('companyAddress', newAddress);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Address Line 1"
                  />

                  <input
                    type="text"
                    value={companyInfo.addressLine2}
                    onChange={(e) => {
                      const newAddress2 = e.target.value;
                      setCompanyInfo(prev => ({...prev, addressLine2: newAddress2}));
                      localStorage.setItem('companyAddress2', newAddress2);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Address Line 2"
                  />
                  
                  <input
                    type="url"
                    value={companyInfo.website}
                    onChange={(e) => {
                      const newWebsite = e.target.value;
                      setCompanyInfo(prev => ({...prev, website: newWebsite}));
                      localStorage.setItem('companyWebsite', newWebsite);
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)]"
                    placeholder="Website (e.g., https://yourfirm.com)"
                  />
                  
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="w-full max-w-2xl bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col gap-6 animate-fadeIn"
          >
            <label htmlFor="notes" className="block text-lg font-semibold text-gray-700 mb-2">
              Paste Client Notes, Emails or Messages:
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={7}
              className="w-full rounded-lg border border-gray-200 p-4 text-base text-gray-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-clickupPurple)] focus:border-[color:var(--color-clickupPurple)] transition resize-vertical bg-gray-50"
              placeholder="Paste raw client notes here or upload documents..."
            />
            
            {/* Live word counter and quality indicator */}
            {(notes.trim() || uploadedFiles.length > 0) && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {uploadedFiles.length > 0 ? (
                      <div>
                        <div>Estimated total: <span className="font-semibold">~{notes.trim().split(/\s+/).length + uploadedWordsEstimate}</span> words</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {notes.trim() ? `${notes.trim().split(/\s+/).length} typed` : '0 typed'}
                          {uploadedWordsEstimate > 0 && ` + ~${uploadedWordsEstimate} estimated from documents`}
                        </div>
                      </div>
                    ) : (
                      <span>Word count: <span className="font-semibold">{notes.trim().split(/\s+/).length}</span></span>
                    )}
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded ${
                    (notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 70 
                      ? 'bg-red-100 text-red-700' 
                      : (notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 120
                      ? 'bg-yellow-100 text-yellow-700'
                      : (notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 200
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {(notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 70 
                      ? `Need ${70 - (notes.trim().split(/\s+/).length + uploadedWordsEstimate)} more words`
                      : (notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 120
                      ? 'Basic'
                      : (notes.trim().split(/\s+/).length + uploadedWordsEstimate) < 200
                      ? 'Good'
                      : 'Excellent'
                    }
                  </span>
                </div>
              </div>
            )}
            
            {/* Removed server-reported Brief Quality UI */}

            <div>
              {uploadedFiles.length > 0 && (
                <div className="mb-4 space-y-2">
                  <h3 className="font-semibold text-gray-700">Uploaded Documents:</h3>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                      <span className="text-sm text-gray-800 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(file)}
                        className="text-red-500 hover:text-red-700 font-bold text-lg ml-2"
                        title="Remove file"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="w-full py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 border border-gray-200 shadow-sm hover:shadow-md transition text-lg tracking-wide flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                {uploadedFiles.length === 0 ? 'Upload a File' : 'Add Another File'}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".txt,.pdf,.doc,.docx,.rtf"
                multiple
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-lg font-bold text-center my-4">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full py-3 rounded-lg font-semibold text-gray-800 bg-gray-100 border border-gray-200 shadow hover:shadow-md transition text-lg tracking-wide disabled:opacity-60 font-inter"
              disabled={loading}
            >
              {loading ? "Generating Brief..." : (pdfUrl ? "Regenerate Brief" : "Generate Brief")}
            </button>
          </form>

          {/* PDF Download & Preview Buttons */}
          {pdfUrl && (
            <div className="w-full max-w-2xl flex flex-row items-center mt-4 gap-4 animate-slideDownSmooth relative">
              <button
                type="button"
                onClick={() => setShowDownloadMenu((s) => !s)}
                className="w-1/2 px-6 py-2 rounded-lg font-semibold text-gray-800 bg-white/80 backdrop-blur-sm border border-gray-200 shadow hover:shadow-md transition text-base tracking-wide disabled:opacity-60 font-inter text-center"
              >
                Download
              </button>
              <button
                id="preview-button"
                type="button"
                onClick={(e) => {
                  setButtonRect(e.currentTarget.getBoundingClientRect());
                }}
                className="w-1/2 px-6 py-2 rounded-lg font-semibold text-gray-800 bg-white/80 backdrop-blur-sm border border-gray-200 shadow hover:shadow-md transition text-base tracking-wide disabled:opacity-60 font-inter text-center"
              >
                Preview PDF
              </button>

              {showDownloadMenu && (
                <div className="absolute left-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg z-20">
                  <button
                    type="button"
                    onClick={() => { downloadPdf(); setShowDownloadMenu(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-t-lg"
                  >
                    Download PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleStripeCheckout(); setShowDownloadMenu(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 text-blue-600 font-medium border-t border-gray-200"
                    disabled={loading}
                  >
                    💳 Purchase Premium Brief ($25)
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowDownloadMenu(false); setError('Word (.docx) download will be available from this menu.'); }}
                    className="w-full text-left px-4 py-2 text-gray-400 cursor-not-allowed rounded-b-lg"
                    disabled
                  >
                    Download Word (.docx) — coming soon
                  </button>
                </div>
              )}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="backdrop-blur-sm border-t border-white/20 py-4 text-center text-white/80 text-sm font-medium">
          &copy; {new Date().getFullYear()} Archibrief. All rights reserved.
        </footer>
      </div>

      {/* 
        This now works safely because it will only render when modalState is not 'closed'.
        The useEffects above guarantee that when modalState is first changed from 'closed',
        we already have a valid buttonRect.
      */}
      {modalState !== 'closed' && (
        <PdfPreviewModal
          pdfUrl={pdfUrl}
          pdfFilename={pdfFilename}
          modalState={modalState}
          setModalState={setModalState}
          buttonRect={buttonRect}
        />
      )}
    </>
  );
}

// New Component for the Modal Logic with a proper state machine
const PdfPreviewModal = ({ pdfUrl, pdfFilename, modalState, setModalState, buttonRect }) => {
  const [showContent, setShowContent] = useState(false);
  const [currentStyle, setCurrentStyle] = useState({});
  const [dimensions, setDimensions] = useState({
    finalWidth: 0,
    finalHeight: 0,
    finalLeft: 0,
    finalTop: 0,
  });

  // Lock body scroll for both 'opening' and 'open' states
  useEffect(() => {
    if (modalState === 'opening' || modalState === 'open') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalState]);

  // Effect to calculate and update dimensions on mount and on window resize
  useEffect(() => {
    const calculateDimensions = () => {
      const finalWidth = Math.min(window.innerWidth * 0.9, 800);
      const finalHeight = window.innerHeight * 0.9;
      const finalLeft = (window.innerWidth / 2) - (finalWidth / 2);
      const finalTop = window.innerHeight * 0.05;
      setDimensions({ finalWidth, finalHeight, finalLeft, finalTop });
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, []);

  const modalBaseStyle = {
    transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms, border-radius 400ms',
    position: 'fixed',
    backgroundColor: '#E5E7EB', // Changed to match the light grey from the image
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transformOrigin: '0 0',
    width: `${dimensions.finalWidth}px`,
    height: `${dimensions.finalHeight}px`,
    top: `${dimensions.finalTop}px`,
    left: `${dimensions.finalLeft}px`,
    borderRadius: modalState === 'preparing' ? '0.5rem' : '0.75rem', // Start with button's radius, end with modal's
  };

  // Calculate the scale and translation needed to make the modal appear like the button
  const scaleX = buttonRect.width / dimensions.finalWidth;
  const scaleY = buttonRect.height / dimensions.finalHeight;
  
  const translateX = buttonRect.left - dimensions.finalLeft;
  const translateY = buttonRect.top - dimensions.finalTop;

  const initialTransform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
  const openTransform = 'translate(0, 0) scale(1)';

  useEffect(() => {
    if (modalState === 'preparing') {
      setCurrentStyle({ transform: initialTransform, opacity: 1 });
      requestAnimationFrame(() => {
        setModalState('opening');
      });
    }

    if (modalState === 'opening') {
      setCurrentStyle({ transform: openTransform, opacity: 1 });
    }

    if (modalState === 'closing') {
      setShowContent(false);
      setCurrentStyle({ transform: initialTransform, opacity: 0 });
    }
  }, [modalState, initialTransform, openTransform, setModalState]);

  const handleTransitionEnd = () => {
    if (modalState === 'opening') {
      setModalState('open');
      setShowContent(true);
    }
    if (modalState === 'closing') {
      setModalState('closed');
    }
  };
  
  return (
    <div 
      className={`fixed inset-0 z-50 transition-opacity duration-400 ${modalState === 'opening' || modalState === 'open' ? 'bg-black/70 backdrop-blur-sm' : 'bg-transparent'}`}
      onClick={() => setModalState('closing')}
    >
      <div
        style={{ ...modalBaseStyle, ...currentStyle }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className={`w-full h-full flex flex-col transition-opacity duration-200 delay-100 rounded-xl overflow-hidden ${showContent ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Header and Content */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-800">PDF Preview</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = pdfFilename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="w-full px-6 py-2 rounded-lg font-semibold text-gray-800 bg-white/80 backdrop-blur-sm border border-gray-200 shadow hover:shadow-md transition text-base tracking-wide disabled:opacity-60 font-inter text-center mr-2"
              >
                Download
              </button>
              <button 
                onClick={() => setModalState('closing')} 
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
              >
                ×
              </button>
            </div>
          </div>
          <div className="flex-1 p-4 overflow-hidden">
            {/* This container will clip the iframe's corners */}
            <div className="w-full h-full rounded-lg overflow-hidden shadow-inner">
              <iframe 
                src={`${pdfUrl}#view=Fit&navpanes=0&toolbar=0&statusbar=0&messages=0`}
                title="PDF Preview" 
                className="w-full h-full bg-white" 
                frameBorder="0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
